import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Target, Sparkles, TrendingUp, Users, DollarSign, Rocket, Zap, Mail } from "lucide-react";
import { CreateLeadDialog } from "@/components/sales/CreateLeadDialog";
import AICampaignGenerator from "@/components/sales/AICampaignGenerator";
import { AnimatedStatCard } from "@/components/sales/AnimatedStatCard";
import { ActivityFeed } from "@/components/sales/ActivityFeed";
import { PipelineMetricsCards } from "@/components/sales/PipelineMetricsCards";
import { AIAssistantPanel } from "@/components/sales/AIAssistantPanel";
import { EmailComposeDialog } from "@/components/email/EmailComposeDialog";
import { LeadCard } from "@/components/leads/LeadCard";
import { LeadDetailPanel } from "@/components/sales/LeadDetailPanel";
import { cn } from "@/lib/utils";
interface StatsData {
  totalLeads: number;
  activeSequences: number;
  replyRate: number;
  demosScheduled: number;
  converted: number;
}

const pipelineStages = [
  { id: 'new', label: 'NEW LEAD', color: 'bg-blue-500', statuses: ['new'] },
  { id: 'contacted', label: 'CONTACTED', color: 'bg-info', statuses: ['contacted'] },
  { id: 'demo_booked', label: 'DEMO BOOKED', color: 'bg-purple-500', statuses: ['qualified'] },
  { id: 'proposal_sent', label: 'PROPOSAL SENT', color: 'bg-orange-500', statuses: ['proposal_sent', 'negotiating'] },
  { id: 'won', label: 'WON', color: 'bg-success', statuses: ['won'] },
];
const SalesCommandCenter = () => {
  const navigate = useNavigate();
  const {
    user,
    orgId,
    loading: authLoading
  } = useAuth();
  const [loading, setLoading] = useState(true);
  const [allLeads, setAllLeads] = useState<any[]>([]);
  const [pipelineLeads, setPipelineLeads] = useState<Record<string, any[]>>({
    new: [],
    contacted: [],
    demo_booked: [],
    proposal_sent: [],
    won: [],
  });
  const [stats, setStats] = useState<StatsData>({
    totalLeads: 0,
    activeSequences: 0,
    replyRate: 0,
    demosScheduled: 0,
    converted: 0
  });
  const [createLeadOpen, setCreateLeadOpen] = useState(false);
  const [aiCampaignOpen, setAiCampaignOpen] = useState(false);
  const [aiAssistantOpen, setAiAssistantOpen] = useState(false);
  const [emailComposeDialogOpen, setEmailComposeDialogOpen] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [leadPanelOpen, setLeadPanelOpen] = useState(false);
  const [draggedLeadId, setDraggedLeadId] = useState<string | null>(null);
  useEffect(() => {
    if (!authLoading && user && orgId) {
      loadData();
    } else if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [authLoading, user, orgId]);
  const loadData = async () => {
    setLoading(true);
    try {
      if (!orgId) return;

      // Load leads
      const {
        data: leads,
        error: leadsError
      } = await supabase.from('leads').select('*').eq('org_id', orgId).order('created_at', {
        ascending: false
      });
      if (leadsError) throw leadsError;
      setAllLeads(leads || []);

      // Categorize leads by pipeline stage
      const categorized: Record<string, any[]> = {
        new: [],
        contacted: [],
        demo_booked: [],
        proposal_sent: [],
        won: [],
      };

      (leads || []).forEach(lead => {
        if (lead.status === 'new') categorized.new.push(lead);
        else if (lead.status === 'contacted') categorized.contacted.push(lead);
        else if (lead.status === 'qualified') categorized.demo_booked.push(lead);
        else if (['proposal_sent', 'negotiating'].includes(lead.status)) categorized.proposal_sent.push(lead);
        else if (lead.status === 'won') categorized.won.push(lead);
      });

      setPipelineLeads(categorized);

      // Calculate stats
      const totalLeads = leads?.length || 0;
      const converted = leads?.filter(l => l.status === 'won').length || 0;
      const demosScheduled = leads?.filter(l => l.status === 'contacted' || l.status === 'qualified').length || 0;

      // Load campaigns for active sequences
      const {
        data: campaigns
      } = await supabase.from('ai_campaigns').select('*').eq('org_id', orgId).eq('status', 'active');
      const activeSequences = campaigns?.length || 0;

      // Calculate reply rate from campaign email sends
      const {
        data: emailSends
      } = await supabase.from('campaign_email_sends').select('replied_at').eq('org_id', orgId);
      const totalEmails = emailSends?.length || 0;
      const replied = emailSends?.filter(e => e.replied_at).length || 0;
      const replyRate = totalEmails > 0 ? Math.round(replied / totalEmails * 100) : 0;
      setStats({
        totalLeads,
        activeSequences,
        replyRate,
        demosScheduled,
        converted
      });
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load sales data');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (leadId: string) => {
    setDraggedLeadId(leadId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (stageId: string) => {
    if (!draggedLeadId) return;
    
    // Map stage ID to actual status value
    const statusMap: Record<string, 'new' | 'contacted' | 'qualified' | 'proposal_sent' | 'negotiating' | 'won' | 'lost'> = {
      'new': 'new',
      'contacted': 'contacted',
      'demo_booked': 'qualified',
      'proposal_sent': 'proposal_sent',
      'won': 'won',
    };
    
    const status = statusMap[stageId];
    if (!status) return;
    
    try {
      const { error } = await supabase
        .from('leads')
        .update({ status })
        .eq('id', draggedLeadId);
      
      if (error) throw error;
      
      toast.success('Lead status updated');
      setDraggedLeadId(null);
      await loadData();
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error('Failed to update lead status');
    }
  };
  
  return <AppLayout>
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        {/* Header */}
        <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="container mx-auto px-6 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold tracking-tight">Sales Hub</h1>
                <p className="text-muted-foreground mt-1">
                  AI-powered sales automation
                </p>
              </div>
              <Button size="lg" onClick={() => setCreateLeadOpen(true)} className="gap-2">
                <Zap className="h-4 w-4" />
                New Lead
              </Button>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-6 py-8 space-y-8">
          {/* Row 1: Quick Stats */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            <AnimatedStatCard title="Total Leads" value={stats.totalLeads} icon={Users} gradient="from-blue-500/10 to-blue-500/5" iconBg="bg-blue-500" />
            <AnimatedStatCard title="Active Campaigns" value={stats.activeSequences} icon={Rocket} gradient="from-purple-500/10 to-purple-500/5" iconBg="bg-purple-500" />
            <AnimatedStatCard title="Reply Rate" value={stats.replyRate} icon={TrendingUp} gradient="from-green-500/10 to-green-500/5" iconBg="bg-green-500" />
            <AnimatedStatCard title="Demos Scheduled" value={stats.demosScheduled} icon={Target} gradient="from-orange-500/10 to-orange-500/5" iconBg="bg-orange-500" />
            <AnimatedStatCard title="Converted" value={stats.converted} icon={DollarSign} gradient="from-emerald-500/10 to-emerald-500/5" iconBg="bg-emerald-500" />
          </div>

          {/* Row 2: Activity Feed & Quick Actions */}
          <div className="grid gap-6 md:grid-cols-2">
            <ActivityFeed />
            
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <Zap className="h-5 w-5 text-green-500" />
                  </div>
                  <div>
                    <CardTitle>Quick Actions</CardTitle>
                    <CardDescription>Launch key workflows</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <Button 
                    size="lg" 
                    className="w-full justify-start gap-3" 
                    onClick={() => setAiCampaignOpen(true)}
                  >
                    <Sparkles className="h-5 w-5" />
                    Generate AI Campaign
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="w-full justify-start gap-3"
                    onClick={() => navigate('/email')}
                  >
                    <Mail className="h-5 w-5" />
                    Manage Sequences
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="w-full justify-start gap-3"
                    onClick={() => setAiAssistantOpen(true)}
                  >
                    <Target className="h-5 w-5" />
                    AI Stats
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Row 3: Pipeline View */}
          <Card>
            <CardHeader>
              <div>
                <CardTitle>Pipeline Overview</CardTitle>
                <CardDescription>Drag leads between stages to update their status</CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 overflow-x-auto pb-4">
                {pipelineStages.map((stage) => (
                  <div
                    key={stage.id}
                    className="flex-shrink-0 w-72"
                    onDragOver={handleDragOver}
                    onDrop={() => handleDrop(stage.id)}
                  >
                    <Card className="h-full">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-sm font-medium uppercase tracking-wide">
                            {stage.label}
                          </CardTitle>
                          <Badge variant="secondary">{pipelineLeads[stage.id]?.length || 0}</Badge>
                        </div>
                        <div className={cn("h-1 rounded-full mt-2", stage.color)} />
                      </CardHeader>
                      <CardContent className="space-y-2 max-h-[750px] overflow-y-auto">
                        {pipelineLeads[stage.id]?.map((lead) => (
                          <div
                            key={lead.id}
                            draggable
                            onDragStart={() => handleDragStart(lead.id)}
                            className="cursor-move"
                          >
                            <LeadCard
                              lead={lead}
                              isSelected={false}
                              onSelect={() => {}}
                              onConvert={() => {}}
                              onCardClick={(id) => {
                                setSelectedLeadId(id);
                                setLeadPanelOpen(true);
                              }}
                              isConverting={false}
                            />
                          </div>
                        ))}
                        {pipelineLeads[stage.id]?.length === 0 && (
                          <div className="text-center py-8 text-sm text-muted-foreground">
                            No leads
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Dialogs */}
        <CreateLeadDialog open={createLeadOpen} onOpenChange={setCreateLeadOpen} onLeadCreated={loadData} />
        
        <AICampaignGenerator open={aiCampaignOpen} onOpenChange={setAiCampaignOpen} />

        <AIAssistantPanel open={aiAssistantOpen} onOpenChange={setAiAssistantOpen} />

        <EmailComposeDialog 
          open={emailComposeDialogOpen} 
          onOpenChange={setEmailComposeDialogOpen}
        />

        {/* Lead Detail Drawer */}
        <Sheet open={leadPanelOpen} onOpenChange={setLeadPanelOpen}>
          <SheetContent side="right" className="w-full sm:max-w-2xl p-0">
            <SheetHeader className="sr-only">
              <SheetTitle>Lead Details</SheetTitle>
            </SheetHeader>
            {selectedLeadId && (
              <LeadDetailPanel
                leadId={selectedLeadId}
                onClose={() => setLeadPanelOpen(false)}
                onConvert={async () => {
                  await loadData();
                  setLeadPanelOpen(false);
                }}
                isConverting={false}
              />
            )}
          </SheetContent>
        </Sheet>
      </div>
    </AppLayout>;
};
export default SalesCommandCenter;